/**
 * Pure presentation of the header XP block (design 1a) — the connected
 * XpHeaderCluster feeds it live data; stories and tests feed it props.
 * Shows the rank as a badge only (no rank-name text); rankName is used as the
 * badge's accessibility label.
 */

import React, { useMemo } from 'react';
import { Platform, Pressable, Text, View, type ImageSourcePropType } from 'react-native';
import { Image } from 'expo-image';
import Svg, { Circle } from 'react-native-svg';
import { FriendAvatar } from '../../../../components/friends/shared/FriendAvatar';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../shared/constants/animations';
import type { DisplayAvatarId } from '../../../avatars/types/avatar.types';
import type { EquippedMap } from '../../../../components/avatars/LayeredAvatarLab/characterCustomizer.types';
import { CELEBRATION_COLORS, XP_HEADER_TEST_IDS } from '../../constants/celebrations.constants';
import {
  getXpHeaderClusterStyles,
  XP_RING_AVATAR_INSET,
  XP_RING_CIRCUMFERENCE,
  XP_RING_RADIUS,
  XP_RING_SIZE,
  XP_RING_STROKE,
} from './XpHeaderCluster.styles';

const RING_TRACK_COLOR = '#2A2438';

export interface XpHeaderClusterViewProps {
  /** Null until the XP summary loads → ring at 0%, no pill, no rank block. */
  level: number | null;
  /** 0..1 progress through the current level. */
  progress: number;
  rankName: string | null;
  rankBadge: ImageSourcePropType | null;
  avatarId: DisplayAvatarId | null;
  avatarConfig: EquippedMap | null;
  onAvatarPress: () => void;
  /** Opens the Rank Ladder (specs/rank-ladder). Omitted → the rank block is not pressable. */
  onRankPress?: () => void;
  testID?: string;
}

export const XpHeaderClusterView: React.FC<XpHeaderClusterViewProps> = ({
  level,
  progress,
  rankName,
  rankBadge,
  avatarId,
  avatarConfig,
  onAvatarPress,
  onRankPress,
  testID,
}) => {
  const styles = useMemo(() => getXpHeaderClusterStyles(), []);
  const dash = Math.min(1, Math.max(0, progress)) * XP_RING_CIRCUMFERENCE;

  return (
    <View style={styles.cluster} testID={testID ?? XP_HEADER_TEST_IDS.CLUSTER}>
      <Pressable
        onPress={onAvatarPress}
        hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
        style={({ pressed, hovered }) => [
          styles.ringBlock,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && styles.pressed,
          hovered && Platform.OS === 'web' && styles.hovered,
        ]}
        accessibilityRole="button"
        accessibilityLabel="Switch account"
        testID="page-header-avatar"
      >
        <Svg
          width={XP_RING_SIZE}
          height={XP_RING_SIZE}
          viewBox={`0 0 ${XP_RING_SIZE} ${XP_RING_SIZE}`}
          style={styles.ringSvg}
        >
          <Circle
            cx={XP_RING_SIZE / 2}
            cy={XP_RING_SIZE / 2}
            r={XP_RING_RADIUS}
            fill="none"
            stroke={RING_TRACK_COLOR}
            strokeWidth={XP_RING_STROKE}
          />
          <Circle
            cx={XP_RING_SIZE / 2}
            cy={XP_RING_SIZE / 2}
            r={XP_RING_RADIUS}
            fill="none"
            stroke={CELEBRATION_COLORS.PURPLE}
            strokeWidth={XP_RING_STROKE}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${XP_RING_CIRCUMFERENCE}`}
          />
        </Svg>
        <View style={styles.avatarWrap}>
          <FriendAvatar
            avatarId={avatarId}
            avatarConfig={avatarConfig}
            size={XP_RING_SIZE - XP_RING_AVATAR_INSET * 2}
            nameForInitials=""
            small
          />
        </View>
        {level != null && (
          <View style={styles.levelPill} testID={XP_HEADER_TEST_IDS.LEVEL_PILL}>
            <Text style={styles.levelPillText}>LV {level}</Text>
          </View>
        )}
      </Pressable>
      {rankBadge != null && (
        <Pressable
          onPress={onRankPress}
          disabled={onRankPress == null}
          hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
          style={({ pressed, hovered }) => [
            styles.rankBlock,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && onRankPress != null && styles.pressed,
            hovered && onRankPress != null && Platform.OS === 'web' && styles.hovered,
          ]}
          accessibilityRole={onRankPress != null ? 'button' : undefined}
          accessibilityLabel={onRankPress != null ? 'View rank ladder' : rankName ?? undefined}
          testID={XP_HEADER_TEST_IDS.RANK_BLOCK}
        >
          <Image
            source={rankBadge}
            style={styles.rankBadge}
            contentFit="contain"
            alt={rankName ?? ''}
          />
        </Pressable>
      )}
    </View>
  );
};
